import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Card, CardBody, Col, Row, Form, Label, Input, Spinner, InputGroup, Button, FormGroup } from 'reactstrap';
import { toast } from 'react-toastify';
import { get, postJson, putJson } from '../../../hook/request';
import { Btn } from '../../../AbstractElements';

type Group = { ID: number; nome: string };

type FormState = {
  typeID: string;
  codice: string;
  email: string;
  password: string;
  cognome: string;
  nome: string;
  data_nascita: string;
  ragione_sociale: string;
  partita_iva: string;
  codice_fiscale: string;
  negozio: string;
  indirizzo_ip: string;
};

function genPassword(): string {
  const lower = 'abcdefghjkmnpqrstuvwxyz';
  const upper = 'ABCDEFGHJKMNPQRSTUVWXYZ';
  const nums = '23456789';
  const syms = '!@#$%^&*?.';
  const pick = (s: string) => s[Math.floor(Math.random() * s.length)];
  const len = 11;
  const base = lower + upper + nums + syms;
  const must = [pick(lower), pick(upper), pick(nums), pick(syms)];
  const rest = Array.from({ length: len - must.length }, () => pick(base));
  const all = [...must, ...rest];
  for (let i = all.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [all[i], all[j]] = [all[j], all[i]];
  }
  return all.join('');
}

export default function UserForm() {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();
  const isEdit = slug && slug !== 'new';
  const id = isEdit ? Number(slug) : null;

  const [groups, setGroups] = useState<Group[]>([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showPwd, setShowPwd] = useState(false);

  const [form, setForm] = useState<FormState>({
    typeID: '',
    codice: '',
    email: '',
    password: '',
    cognome: '',
    nome: '',
    data_nascita: '',
    ragione_sociale: '',
    partita_iva: '',
    codice_fiscale: '',
    negozio: '',
    indirizzo_ip: '',
  });

  // carica gruppi
  useEffect(() => {
    (async () => {
      try {
        setLoadingGroups(true);
        const res = await get<any>('permissions/group?type=all');
        const payload = (res as any)?.data ?? res;
        setGroups(Array.isArray(payload) ? payload : []);
      } catch {
        toast.error('Impossibile caricare le tipologie utente');
      } finally {
        setLoadingGroups(false);
      }
    })();
  }, []);

  // carica utente se edit
  useEffect(() => {
    if (!isEdit || !id) return;
    (async () => {
      try {
        setLoading(true);
        const res = await get<any>(`/users/detail?ID=${id}`);
        const data = (res as any)?.data ?? res;
        setForm({
          typeID: String(data.typeID ?? ''),
          codice: data.codice ?? '',
          email: data.email ?? '',
          password: data.password ?? '',
          cognome: data.cognome ?? '',
          nome: data.nome ?? '',
          data_nascita: data.nascita_data ?? '',
          ragione_sociale: data.ragionesociale ?? '',
          partita_iva: data.piva ?? '',
          codice_fiscale: data.cf ?? '',
          negozio: data.negozio ?? '',
          indirizzo_ip: data.indirizzo_ip ?? '',
        });
      } catch (e: any) {
        toast.error('Errore nel caricamento utente');
      } finally {
        setLoading(false);
      }
    })();
  }, [isEdit, id]);

  const canSubmit = useMemo(() => {
    return (
      !!form.typeID &&
      !!form.email.trim() &&
      !!form.nome.trim() &&
      !!form.cognome.trim()
    );
  }, [form]);

  const onChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) {
      toast.warn('Compila i campi obbligatori.');
      return;
    }

    const payload: any = {
      typeID: Number(form.typeID),
      codice: form.codice || null,
      email: form.email,
      cognome: form.cognome,
      nome: form.nome,
      data_nascita: form.data_nascita || null,
      ragione_sociale: form.ragione_sociale || null,
      partita_iva: form.partita_iva || null,
      codice_fiscale: form.codice_fiscale || null,
      negozio: form.negozio || null,
      indirizzo_ip: form.indirizzo_ip || null,
      password: form.password || null
    };


    try {
      setSaving(true);
      if (isEdit && id) {
        await putJson(`/users/edit?ID=${id}`, payload);
        toast.success('Utente aggiornato con successo');
      } else {
        await postJson('/users/create', payload);
        toast.success('Utente creato con successo');
      }

    } catch (e: any) {
      const msg = e?.response?.data?.message ?? e?.message ?? 'Errore nel salvataggio';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="page-body pt-4">
        <Card>
          <CardBody className="d-flex justify-content-center py-5">
            <Spinner />
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <div className="page-body pt-4">

      <Col xl={12} className="mb-lg-3">
        <Card>
          <CardBody>
            <Form onSubmit={onSubmit}>
            <div className='d-flex justify-content-between align-items-center'>
              <h4 className="mb-3">
                {isEdit ? 'Modifica Utente' : 'Crea Nuovo Utente'}
              </h4>
              <Link to="/configurazione/users">
                <Btn className="px-4" color="primary">Torna Indietro</Btn>
              </Link>
            </div>

            {/* Tipologia utente */}
            <Row className="g-3 mb-2 mt-2">
              <Col lg={12}>
                <Label>Tipologia utente</Label>
                <Input
                  type="select"
                  name="typeID"
                  value={form.typeID}
                  onChange={onChange}
                  disabled={loadingGroups}
                >
                  <option value="">{loadingGroups ? 'Caricamento…' : 'Scegli tipologia…'}</option>
                  {groups
                    .filter(g => g.ID !== 1)
                    .map(g => (
                      <option key={g.ID} value={g.ID}>
                        {g.nome}
                      </option>
                    ))}
                </Input>
              </Col>
            </Row>

            {/* Profilo */}
            <hr></hr>
            <h4 className="mt-3 mb-2">Profilo</h4>
            <Row className="g-3">
              <Col lg={4}>
              <FormGroup>
                <Label>Codice</Label>
                <Input name="codice" placeholder="Codice" value={form.codice} onChange={onChange} />
                </FormGroup>
              </Col>
              <Col lg={4}>
                <Label>Email</Label>
                <Input type="email" name="email" placeholder="email@dominio.it" value={form.email} onChange={onChange} />
              </Col>
              <Col lg={4}>
                <Label>Password</Label>
                <InputGroup className='d-flex gap-1'>
                  <Input
                    type={showPwd ? 'text' : 'password'}
                    name="password"
                    placeholder="Imposta password"
                    value={form.password}
                    onChange={onChange}
                  />
                  <Btn style={{borderRadius:5}} color="warning" outline onClick={() => setShowPwd(s => !s)} type="button">
                    {showPwd ? 'Nascondi' : 'Mostra'}
                  </Btn>
                  <Button
                    color="info"
                    onClick={() => setForm(p => ({ ...p, password: genPassword() }))}
                    type="button"
                  >
                    Genera
                  </Button>
                </InputGroup>
              </Col>
            </Row>

            {/* Anagrafica */}
            <hr></hr>
            <h4 className="mt-4 mb-2">Anagrafica</h4>
            <Row className="g-3">
              <Col lg={4}>
                <Label>Cognome</Label>
                <Input name="cognome" value={form.cognome} onChange={onChange} />
              </Col>
              <Col lg={4}>
                <Label>Nome</Label>
                <Input name="nome" value={form.nome} onChange={onChange} />
              </Col>
              <Col lg={4}>
                <Label>Data di nascita</Label>
                <Input type="date" name="data_nascita" value={form.data_nascita} onChange={onChange} />
              </Col>
              <Col lg={6}>
                <Label>Ragione sociale</Label>
                <Input name="ragione_sociale" value={form.ragione_sociale} onChange={onChange} />
              </Col>
              <Col lg={3}>
                <Label>Partita IVA</Label>
                <Input name="partita_iva" value={form.partita_iva} onChange={onChange} />
              </Col>
              <Col lg={3}>
                <Label>Codice Fiscale</Label>
                <Input name="codice_fiscale" value={form.codice_fiscale} onChange={onChange} />
              </Col>
            </Row>
            <hr></hr>
            <h4 className="mt-4 mb-2">Altro</h4>
            <Row className="g-3">
              <Col lg={6}>
                <Label>Negozio</Label>
                <Input name="negozio" placeholder='Codice Negozio' value={form.negozio} onChange={onChange} />
              </Col>
              <Col lg={6}>
                <Label>Indirizzo IP</Label>
                <Input name="indirizzo_ip" placeholder='192.68.0.0' value={form.indirizzo_ip} onChange={onChange} />
              </Col>
            </Row>

            <Row className="g-3 mt-4">
              <Col xs={12} className="d-flex gap-2">
                <Btn color="primary" className="px-4" type="submit" disabled={!canSubmit || saving}>
                  {saving ? <Spinner size="sm" /> : isEdit ? 'Aggiorna' : 'Crea'}
                </Btn>
                <Link to="/configurazione/users">
                  <Btn color="danger" className="px-4" type="button">Annulla</Btn>
                </Link>
              </Col>
            </Row>
          </Form>
        </CardBody>
      </Card>
    </Col>
     
    </div >
  );
}
